import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  FaCalendarAlt,
  FaClock,
  FaBook,
  FaUserGraduate,
  FaTimesCircle,
  FaEye,
  FaStar,
  FaFilter
} from 'react-icons/fa';
import Navbar from '../layout/Navbar';
import '../../styles/dashboard.css';

const StudentBookings = () => {
  const { user, userData } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, upcoming, completed, pending, cancelled

  const location = useLocation();
  useEffect(() => {
    if (user) {
      fetchBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filter, location.search]);

  const fetchBookings = async () => {
    try {
      // Determine whether current user is a student or a tutor and build query accordingly
      const isTutor = userData?.userType === 'tutor';
      const baseWhere = isTutor ? where('tutorId', '==', user.uid) : where('studentId', '==', user.uid);
      const bookingsQuery = query(
        collection(db, 'bookings'),
        baseWhere,
        orderBy('date', 'desc')
      );

      // If a tutor is viewing and a studentId is provided in query params, filter client-side
      const params = new URLSearchParams(location.search);
      const qStudentId = params.get('studentId');

      const snapshot = await getDocs(bookingsQuery);
      let bookingsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Apply filter
      if (filter !== 'all') {
        bookingsData = bookingsData.filter(booking => {
          switch (filter) {
            case 'upcoming':
              return new Date(booking.date) > new Date() && booking.status === 'confirmed';
            case 'completed':
              return booking.status === 'completed';
            case 'pending':
              return booking.status === 'pending';
            case 'cancelled':
              return booking.status === 'cancelled';
            default:
              return true;
          }
        });
      }

      setBookings(bookingsData);
      // If tutor provided a studentId query param, further filter client-side
      if (qStudentId && isTutor) {
        setBookings(prev => prev.filter(b => b.studentId === qStudentId));
      } else {
        setBookings(bookingsData);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusClass = (status) => {
    // return class suffix matching CSS: confirmed, pending, completed, cancelled
    if (!status) return 'unknown';
    return String(status).toLowerCase();
  };

  const handleCancelBooking = async (bookingId) => {
    if (window.confirm('Are you sure you want to cancel this booking?')) {
      try {
          await updateDoc(doc(db, 'bookings', bookingId), {
            status: 'cancelled',
            updatedAt: new Date()
          });
        fetchBookings(); // Refresh the list
      } catch (error) {
        console.error('Error cancelling booking:', error);
        alert('Failed to cancel booking. Please try again.');
      }
    }
  };

  const parseDate = (d) => {
    if (!d) return null;
    // handle Firestore Timestamp
    if (d.toDate && typeof d.toDate === 'function') return d.toDate();
    return new Date(d);
  };

  const formatDate = (date) => {
    const dt = parseDate(date);
    if (!dt) return '';
    return dt.toLocaleDateString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    const dt = parseDate(date);
    if (!dt) return '';
    return dt.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="loading">
        Loading your bookings...
      </div>
    );
  }

  return (
    <div className="dashboard-root">
      <Navbar />
      <div className="dashboard-container">
        <div className="dashboard-sidebar">
          <div className="user-profile-card">
            <div className="user-avatar">
              {userData?.fullName?.charAt(0) || 'U'}
            </div>
            <div className="user-info">
              <h3>{userData?.fullName || 'User'}</h3>
              <p className="user-role">{userData?.userType?.toUpperCase()}</p>
              <p className="user-location">{userData?.location}</p>
            </div>
          </div>

          <nav className="dashboard-nav">
            <Link to="/dashboard" className="nav-item">
              <FaUserGraduate /> Overview
            </Link>
            {userData?.userType === 'student' ? (
              <>
                <Link to="/dashboard/bookings" className="nav-item active">
                  <FaCalendarAlt /> My Bookings
                </Link>
                <Link to="/dashboard/tutors" className="nav-item">
                  <FaUserGraduate /> My Tutors
                </Link>
              </>
            ) : (
              <>
                <Link to="/dashboard/schedule" className="nav-item">
                  <FaCalendarAlt /> My Schedule
                </Link>
                <Link to="/dashboard/students" className="nav-item">
                  <FaUserGraduate /> My Students
                </Link>
                <Link to="/dashboard/earnings" className="nav-item">
                  <FaBook /> Earnings
                </Link>
              </>
            )}
            <Link to="/dashboard/reviews" className="nav-item">
              <FaStar /> Reviews
            </Link>
            <Link to="/dashboard/messages" className="nav-item">
              <FaClock /> Messages
            </Link>
            <Link to="/dashboard/settings" className="nav-item">
              <FaUserGraduate /> Settings
            </Link>
          </nav>
        </div>

        <div className="dashboard-main">
          <div className="dashboard-header">
            <h1>My Bookings</h1>
            <p>Manage your tutoring sessions</p>
          </div>

          {/* Filter Buttons */}
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({bookings.length})
            </button>
            <button
              className={`filter-btn ${filter === 'upcoming' ? 'active' : ''}`}
              onClick={() => setFilter('upcoming')}
            >
              Upcoming
            </button>
            <button
              className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
              onClick={() => setFilter('completed')}
            >
              Completed
            </button>
            <button
              className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => setFilter('pending')}
            >
              Pending
            </button>
          </div>

          {/* Bookings List */}
          {bookings.length > 0 ? (
            <div className="bookings-list">
              {bookings.map(booking => (
                <div key={booking.id} className="booking-card">
                  <div className="booking-header">
                    <div className="booking-info">
                      <h3>{booking.tutorName}</h3>
                      <p className="subject">{booking.subject} - {booking.level}</p>
                      <div className="booking-meta">
                        <span className="date">
                          <FaCalendarAlt /> {formatDate(booking.date)}
                        </span>
                        <span className="time">
                          <FaClock /> {formatTime(booking.date)}
                        </span>
                        <span className="duration">
                          {booking.duration} hour{booking.duration > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="booking-status">
                      <span className={`status-badge status-${getStatusClass(booking.status)}`}>
                        {(booking.status || '').charAt(0).toUpperCase() + (booking.status || '').slice(1)}
                      </span>
                    </div>
                  </div>

                  <div className="booking-details">
                    <p><strong>Meeting Type:</strong> {booking.meetingType}</p>
                    {booking.location && (
                      <p><strong>Location:</strong> {booking.location}</p>
                    )}
                    {booking.notes && (
                      <p><strong>Notes:</strong> {booking.notes}</p>
                    )}
                    <p><strong>Total Amount:</strong> ${booking.totalAmount}</p>
                  </div>

                  <div className="booking-actions">
                    <button
                      className="btn-outline btn-small"
                      onClick={() => navigate(`/booking/${booking.id}`)}
                    >
                      <FaEye /> View Details
                    </button>

                    {booking.status === 'confirmed' && new Date(booking.date) > new Date() && (
                      <button
                        className="btn-danger btn-small"
                        onClick={() => handleCancelBooking(booking.id)}
                      >
                        <FaTimesCircle /> Cancel
                      </button>
                    )}

                    {booking.status === 'completed' && (
                      <button
                        className="btn-primary btn-small"
                        onClick={() => navigate(`/dashboard/reviews?booking=${booking.id}`)}
                      >
                        <FaStar /> Leave Review
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <FaCalendarAlt className="empty-icon" />
              <h3>No bookings found</h3>
              <p>
                {filter === 'all'
                  ? "You haven't booked any sessions yet."
                  : `No ${filter} bookings found.`
                }
              </p>
              <button
                className="btn-primary"
                onClick={() => navigate('/search')}
              >
                Find a Tutor
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentBookings;